import React, { useState, useEffect, useRef, useContext } from 'react';
import { useChat } from './ChatContext';
import { AuthContext } from '../Provider';
import { FaRegCommentDots } from "react-icons/fa";
import ConversationsList from './ConversationsList';
import ChatWindow from './ChatWindow';
import NotificationDisplay from './NotificationDisplay';
import useRideNotifications from './RideNotifications';
import { supabase } from '../data/supabaseClient';
import { useTranslation } from 'react-i18next'; // Import translation hook
import Spinner from '../common/Spinner'; // Import the new Spinner component
import { Button } from '../Templates';
import './ChatV2.css';

const ChatV2 = () => {
  // Get translation function
  const { t } = useTranslation('common');
  
  // Get user from AuthContext
  const { user, loading: authLoading } = useContext(AuthContext);
  
  // Get necessary values from ChatContext
  const {
    isChatOpen,
    toggleChat,
    activeConversationId,
    openChatWithConversation,
    markConversationAsRead,
    addOtherUnreadMessage,
    otherUnreadMessages,
    clearOtherUnreadMessage,
    occurrenceContext // Get occurrence context for recurring posts
  } = useChat();

  // Local state
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const messageSubscriptionRef = useRef(null);

  // Get current user and set up message subscription
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          throw userError;
        }
        
        setCurrentUser(user);

        if (user) {
          // Set up message subscription with better unread tracking
          const channel = supabase
            .channel('global-messages')
            .on('postgres_changes', 
              {
                event: 'INSERT',
                schema: 'public',
                table: 'messages'
              },
              async (payload) => {
                // Only proceed if message is not from current user
                if (payload.new.user_id === user.id) return;

                try {
                  // Check if this is for a conversation we're participating in
                  const { data: participant, error: participantError } = await supabase
                    .from('conversation_participants')
                    .select('conversation_id')
                    .eq('conversation_id', payload.new.conversation_id)
                    .eq('user_id', user.id)
                    .single();

                  if (participantError && participantError.code !== 'PGRST116') {
                    throw participantError;
                  }

                  if (participant) {
                    // Only add to unread if chat is closed OR we're in a different conversation
                    if (!isChatOpen || payload.new.conversation_id !== activeConversationId) {
                      addOtherUnreadMessage(payload.new.conversation_id);
                    }
                  }
                } catch (error) {
                  console.error('Error processing incoming message:', error);
                }
              }
            )
            .subscribe();

          messageSubscriptionRef.current = channel;
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    // Cleanup subscription
    return () => {
      if (messageSubscriptionRef.current) {
        supabase.removeChannel(messageSubscriptionRef.current);
      }
    };
  }, [activeConversationId, addOtherUnreadMessage, isChatOpen]);

  // Initialize notifications
  useRideNotifications(currentUser?.id);

  // Handle conversation selection
  const handleConversationSelect = (conversationId) => {
    setSelectedConversation(conversationId);
    markConversationAsRead(conversationId);
    openChatWithConversation(conversationId);
    clearOtherUnreadMessage(conversationId);
  };
  
  // Clear unread messages when opening chat or switching conversations
  useEffect(() => {
    if (isChatOpen && activeConversationId) {
      clearOtherUnreadMessage(activeConversationId);
    }
  }, [isChatOpen, activeConversationId, clearOtherUnreadMessage]);

  // Handle back button click
  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  // Effect to mark conversations as read when selected
  useEffect(() => {
    if (selectedConversation) {
      markConversationAsRead(selectedConversation);
      clearOtherUnreadMessage(selectedConversation);
    }
  }, [selectedConversation, markConversationAsRead, clearOtherUnreadMessage]);

  // Clear all unread messages when chat is opened to conversations list
  useEffect(() => {
    if (isChatOpen && !activeConversationId && !selectedConversation) {
      // User opened chat but no specific conversation - clear notifications
      const unreadArray = Array.from(otherUnreadMessages);
      unreadArray.forEach(conversationId => {
        clearOtherUnreadMessage(conversationId);
      });
    }
  }, [isChatOpen, activeConversationId, selectedConversation, otherUnreadMessages, clearOtherUnreadMessage]);

  // Determine what to render in the chat container
  const renderChatContent = () => {
    // If there's an error, show error message
    if (error) {
      return (
        <div className="chat-error">
          <p>{t('chat.error')}: {error}</p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {t('buttons.retry')}
          </Button>
        </div>
      );
    }

    // If still loading, show spinner
    if (isLoading) {
      return (
        <div className="chat-loading">
          <Spinner size="medium" customText={t('chat.loading')} />
        </div>
      );
    }

    // If there's an active conversation (coming from post), show it directly
    if (activeConversationId) {
      return (
        <ChatWindow
          conversationId={activeConversationId}
          onBack={handleBackToList}
          occurrenceContext={occurrenceContext}
        />
      );
    }

    // If there's a selected conversation from the list, show it
    if (selectedConversation) {
      return (
        <ChatWindow
          conversationId={selectedConversation}
          onBack={handleBackToList}
          occurrenceContext={occurrenceContext}
        />
      );
    }

    // Otherwise show the conversations list
    return <ConversationsList onSelect={handleConversationSelect} />;
  };

  // Don't render anything if user is not authenticated
  if (!user || authLoading) {
    return null;
  }

  return (
    <>
      {/* Chat container */}
      {isChatOpen && (
        <div className="chat-container">
          {renderChatContent()}
        </div>
      )}

      {/* Chat toggle button */}
      <div className="chat-toggle-container">
        <button 
          className="chat-toggle-button" 
          onClick={toggleChat}
          aria-label={t('chat.toggle_chat')}
        >
          <FaRegCommentDots size={24} />
          {otherUnreadMessages.size > 0 && (
            <div className="unread-count">{otherUnreadMessages.size}</div>
          )}
        </button>
      </div>
    </>
  );
};

export default ChatV2;